/**
 * Part 15 — Conditional rendering based on the viewer's effective ACL role
 * on a resource.
 *
 * Usage:
 *   <ResourceGate entityType="project" entityId={pid} minRole="manager">
 *     <DeleteProjectButton />
 *   </ResourceGate>
 *
 * Behavior:
 *   - While loading the effective role, nothing renders (avoids button flash).
 *   - If the user's effective role meets `minRole`, the children render.
 *   - Otherwise, `fallback` renders (default: nothing).
 *
 * For open-mode orgs (the default) where most users hold no grant, callers
 * should pass `allowWhenNoRole` to fall back to global RBAC. This stays
 * backward-compatible with deployments that haven't opted into strict ACL.
 */
import type { ReactNode } from 'react'

import { useEffectiveRole } from '../../hooks/useEffectiveRole'
import type { ACLEntityType, ACLRole } from '../../services/permissions'

interface Props {
  entityType: ACLEntityType
  entityId: number | null | undefined
  minRole: ACLRole
  children: ReactNode
  fallback?: ReactNode
  /**
   * If true (default), users with no explicit grant see `children` — this
   * preserves the legacy `open`-mode behavior where RBAC is the only gate.
   * Set to false on strict-mode pages where lack of a grant must hide UI.
   */
  allowWhenNoRole?: boolean
}

export function ResourceGate({
  entityType,
  entityId,
  minRole,
  children,
  fallback = null,
  allowWhenNoRole = true,
}: Props) {
  const { role, loading, canAtLeast } = useEffectiveRole(entityType, entityId)

  if (loading) return null
  if (canAtLeast(minRole)) return <>{children}</>
  if (allowWhenNoRole && role == null) return <>{children}</>
  return <>{fallback}</>
}
